import { makeObservable, observable, action, runInAction } from "mobx";
import {
  formatDurationLimitLabel,
  formatElapsedDuration,
} from "@/store/audio-capture/utils/duration-formatting";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

export class UiTelemetryManager {
  friendlyDebugInfo: Record<string, unknown> = {};
  heartbeatForComputedValues = Date.now();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly store: AppStoreAudioCaptureStore) {
    makeObservable<this, "heartbeatTimer">(this, {
      heartbeatTimer: true,
      friendlyDebugInfo: observable,
      heartbeatForComputedValues: observable,
      updateFriendlyDebugInfo: action,
      resetFriendlyDebugInfo: action,
      startHeartbeat: action,
      stopHeartbeat: action,
    });

    this.startHeartbeat();
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      runInAction(() => {
        this.heartbeatForComputedValues = Date.now();
      });
    }, 2_000);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  updateFriendlyDebugInfo(extra?: Record<string, unknown>) {
    const maxRecordingDurationSeconds = Math.floor(this.store.MAX_RECORDING_DURATION.as("seconds"));
    const safeExtra = extra ?? {};

    this.friendlyDebugInfo = {
      ...(this.friendlyDebugInfo ?? {}),
      status: this.store.status,
      durationSeconds: this.store.duration,
      totalDurationSeconds: this.store.duration + this.store.restoredDuration,
      formattedDuration: this.store.formattedDuration,
      maxRecordingDurationSeconds,
      maxRecordingDurationISO: this.store.MAX_RECORDING_DURATION.toISO(),
      maxRecordingDurationLabel: formatDurationLimitLabel(this.store.MAX_RECORDING_DURATION),
      remainingSeconds: Math.max(0, maxRecordingDurationSeconds - this.store.duration),
      autoStopReason: this.store.autoStopReason,
      autoStopModalVisible: this.store.autoStopModalVisible,
      autoStopDurationSeconds: this.store.autoStopDurationSeconds,
      autoStopDurationLabel: formatElapsedDuration(this.store.autoStopDurationSeconds),
      ...safeExtra,
    };
  }

  resetFriendlyDebugInfo() {
    this.friendlyDebugInfo = {};
  }
}
